import { TONES } from "../../shared/tones";
import { titleCase } from "../../shared/format";

// ─── Popup theme helpers (shared across every overlay in the Work page) ────────
// One definition so the Move picker, Delete confirmation, and Save Preset modal
// stay consistent in both light and dark mode. `border` is included in
// popupBorder so the border actually renders (color alone shows nothing).
export const popupBg = "bg-white dark:bg-gray-800";
export const popupBorder = "border border-gray-200 dark:border-gray-700";
export const popupText = "text-gray-900 dark:text-gray-200";
export const popupSubtext = "text-gray-500 dark:text-gray-400";
export const popupBtn = "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700";
export const popupDestructive = "text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20";
export const popupInput = "bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200";

// ─── Priority options for the create-task modal ─────────────────────────────────
// Static — hoisted to module scope so the array/object are NOT reallocated on every
// keystroke re-render, satisfying the "memoize dropdown option lists" requirement in
// the tracker. Used only by CreateTaskModal.
export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export const PRIORITY_LABELS = { low: "Low", medium: "Medium", high: "High", urgent: "Urgent" };

// ─── Sort options ─────────────────────────────────────────────────────────────
// Drives the "Sort" dropdown next to the Group-by control in WorkFilters. Each
// entry is a { key, label } pair; `key` is matched against the sortField state in
// WorkPage to select a client-side comparator (see the comparators defined below
// this block / in the sort-logic step). Hoisted to module scope so the array is
// NOT reallocated on every re-render, matching the memoize-options convention
// used by PRIORITY_OPTIONS and GROUP_BY_OPTIONS.
export const SORT_OPTIONS = [
  { key: "urgency", label: "Urgency" },
  { key: "due_date", label: "Due date" },
  { key: "priority", label: "Priority" },
  { key: "days_in_status", label: "Days in status" },
  { key: "owner", label: "Owner" },
  { key: "title", label: "Task title" },
];

// ─── Buckets ───────────────────────────────────────────────────────────────────
export const BUCKETS = [
  { key: "unowned",         label: "Unowned"          },
  { key: "waiting_on_us",   label: "Waiting on Us"    },
  { key: "waiting_on_them", label: "Waiting on Them"  },
  { key: "waiting_on_me",   label: "Mine"             },
];

export const DEFAULT_BUCKET = "waiting_on_us";

// Hardcoded fallback for the pipeline dropdown when fn ops_work_types is
// unreachable (RLS gap, cold start, etc.) so the filter never renders empty.
export const FALLBACK_WORK_TYPES = [
  { code: "delivery",      name: "Delivery" },
  { code: "editorial",     name: "Editorial" },
  { code: "guest_booking", name: "Guest Booking" },
  { code: "hr",            name: "HR" },
  { code: "internal",      name: "Internal" },
  { code: "lead",          name: "Lead" },
  { code: "onboarding",    name: "Onboarding" },
  { code: "outreach",      name: "Outreach" },
  { code: "recruiting",     name: "Recruiting" },
];

// ─── Bucket descriptions ──────────────────────────────────────────────────────
export const BUCKET_DESCRIPTIONS = {
  unowned:         "Nobody has claimed this work. Unowned work cannot be forecast, chased, or finished.",
  waiting_on_us:   "A colleague has it, or someone raised their hand. This is blocked on us.",
  waiting_on_them: "Blocked on the client. This is where 'how long do clients keep us waiting' becomes answerable.",
  waiting_on_me:   "This work is assigned to you.",
};

// ─── Group-by options ──────────────────────────────────────────────────────────
// Registry of grouping axes for the task list. Each entry drives the collapsible
// sections in WorkPage: groupFn maps an item to a raw group key (or null for a
// flat list), and displayNames maps raw keys to natural-language headers. Unknown
// raw keys fall back to a title-cased version of the raw value (underscores →
// spaces) via getGroupDisplayName. The previous { key, label }-only entries
// (bucket/owner/client/pipeline/service) were unused in source and superseded
// by this richer shape — the RPC's p_group_by still accepts those values directly.
export const GROUP_BY_OPTIONS = [
  {
    key: "none",
    label: "No Grouping",
    groupFn: null,
    displayNames: {},
  },
  {
    key: "status",
    label: "Status",
    // Returns the raw item.status — known or unknown. Unknown values are not
    // mapped here; getGroupDisplayName title-cases them at render time.
    groupFn: (item) => (item ? item.status : undefined),
    displayNames: {
      todo: "To Do",
      in_progress: "In Progress",
      new_idea: "New Idea",
      waiting: "Waiting",
      blocked: "Blocked",
      done: "Done",
    },
  },
  {
    key: "due_date",
    label: "Due Date",
    // Buckets item.due into overdue / today / this_week / later / no_date.
    groupFn: (item) => {
      if (!item || !item.due) return "no_date";
      const now = new Date();
      const due = new Date(item.due);
      const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
      if (diff < 0) return "overdue";
      if (diff === 0) return "today";
      if (diff <= 7) return "this_week";
      return "later";
    },
    displayNames: {
      overdue: "Overdue",
      today: "Due Today",
      this_week: "Due This Week",
      later: "Later",
      no_date: "No Due Date",
    },
  },
  {
    key: "client",
    label: "Client",
    // Client names are dynamic (not a fixed set), so no displayNames mapping is
    // needed — getGroupDisplayName falls back to the raw value, which is the
    // display name. Null/missing clients group under "Internal".
    groupFn: (item) => (item ? item.client || "Internal" : "Internal"),
    displayNames: {},
  },
];

// Look up the display name for a raw group key within the given grouping axis.
// Finds the matching GROUP_BY_OPTIONS entry by axisKey, then looks up rawKey in
// its displayNames. If no match (unknown axis or unknown raw key), falls back to
// a title-cased version of rawKey (underscores → spaces) so snake_case keys still
// render as natural language. Null/undefined rawKey passes through unchanged.
export const getGroupDisplayName = (axisKey, rawKey) => {
  if (rawKey == null) return rawKey;
  const option = GROUP_BY_OPTIONS.find((opt) => opt.key === axisKey);
  if (option && option.displayNames && option.displayNames[rawKey]) {
    return option.displayNames[rawKey];
  }
  return titleCase(String(rawKey).replace(/_/g, " "));
};

// ─── Tint colours for signals ─────────────────────────────────────────────────
export const SIGNAL_COLORS = {
  broken_promises: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  stalled:         "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  needs_assistance:"bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  recent_messages: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
};

// ─── Takeaway tint colours ─────────────────────────────────────────────────────
export const TINT_STYLES = {
  warm:     "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
  cool:     "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
  positive: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800",
};

// ─── Verdict tint colours ───────────────────────────────────────────────────────
export const VERDINT_TINTS = {
  warm:     TONES.gray,
  cool:     TONES.gray,
  positive: TONES.gray,
  alert:    TONES.red,
};

// ─── Service tag colours (deterministic per service string) ────────────────────
export const SERVICE_TAG_STYLES = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
];

// ─── Service pill colours (by service_code) ───────────────────────────────────
// Deterministic per-code hex used as the inline pill background (Item 3). Unlike
// SERVICE_TAG_STYLES above (a hash-into-class pool for InboxRow), this is a
// fixed code→hex map so a given service always gets the same colour in the
// slide-out. Unknown codes fall back to a neutral gray via the 'default' key.
export const SERVICE_COLORS = {
  website_design: "#8b5cf6",
  google_ads: "#22c55e",
  seo: "#3b82f6",
  social_media: "#f59e0b",
  email_marketing: "#ef4444",
  content_writing: "#06b6d4",
  paid_ads: "#f97316",
  video_production: "#ec4899",
  default: "#6b7280",
};

/** Resolve a service_code to its pill hex. Null/undefined or unknown codes
 *  return the neutral default. Mirrors the priority pill's backend-hex path. */
export const getServiceColor = (serviceCode) =>
  (serviceCode != null && SERVICE_COLORS[serviceCode]) || SERVICE_COLORS["default"];

// ─── RPC error code → friendly message ─────────────────────────────────────────
export const RPC_ERROR_MESSAGES = {
  "57014": "The request took too long. Try narrowing your filters.",
  "57P03": "Could not connect to the server. Please try again later.",
  "42501": "You don't have permission to access this data.",
  "42P01": "Work data not found for this account.",
  "28P01": "Authentication failed. Please try again.",
  "PGRST204": "The requested data format is not available.",
};

// ─── Status display names ──────────────────────────────────────────────────────
// Raw DB status values (snake_case) → natural language for display. Known statuses
// get an exact label; anything unknown falls back to titleCase of the underscore-
// replaced value so a new status like "in_review" still renders as "In Review"
// rather than the raw snake_case. Display-only — the data layer keeps raw values.
const STATUS_DISPLAY_NAMES = {
  todo: "To Do",
  in_progress: "In Progress",
  new_idea: "New Idea",
  waiting: "Waiting",
  blocked: "Blocked",
  done: "Done",
};

export const statusDisplayName = (status) => {
  if (status == null) return status;
  const key = String(status);
  if (STATUS_DISPLAY_NAMES[key]) return STATUS_DISPLAY_NAMES[key];
  return titleCase(key.replace(/_/g, " "));
};

// Add to src/components/hub/work/constants.js
export const statusPillClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "needs_assistance" || s === "blocked" || s === "stalled")
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
  if (s === "done" || s === "completed" || s === "closed" || s === "resolved")
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
  if (s.includes("overdue"))
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
  return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
};

// ─── Urgency sort comparator ──────────────────────────────────────────────────
// Orders task items within a bucket by urgency so the most attention-worthy
// cards surface to the top. All fields are already present in the fn_work_list
// response — this is a pure client-side sort, no backend changes needed.
//
// Comparison order (first differentiator wins), all descending by default:
//   1. broken_promises  — higher count first (descending)
//   2. stalled          — true before false
//   3. days_in_status   — higher first (descending)
//   4. fully tied       — return 0 (stable)
//
// Direction (Item 4 — urgency sort ascend/descend): pass direction="asc" to
// reverse the result so the least-urgent items surface first. direction
// defaults to "desc" (most urgent first), preserving the historical default.
//
// Usage (default, descending):  items.slice().sort(sortItemsByUrgency)
// Usage (ascending):             items.slice().sort((a, b) => sortItemsByUrgency(a, b, "asc"))
export const sortItemsByUrgency = (a, b, direction = "desc") => {
  // Compute the descending-order comparison (most urgent first), then flip the
  // sign when ascending is requested. Centralizing the flip at the end keeps the
  // multi-field tiebreaker logic readable and unchanged.
  const desc = (() => {
    // 1. broken_promises — higher first
    const aBroken = a?.broken_promises ?? 0;
    const bBroken = b?.broken_promises ?? 0;
    if (bBroken !== aBroken) {
      return bBroken - aBroken;
    }

    // 2. stalled — true before false
    const aStalled = a?.stalled === true;
    const bStalled = b?.stalled === true;
    if (aStalled !== bStalled) {
      return aStalled ? -1 : 1;
    }

    // 3. days_in_status — higher first
    const aDays = a?.days_in_status ?? 0;
    const bDays = b?.days_in_status ?? 0;
    if (bDays !== aDays) {
      return bDays - aDays;
    }

    // 4. fully tied
    return 0;
  })();

  return direction === "asc" ? -desc : desc;
};

// ─── Sort comparators (one per SORT_OPTIONS entry) ─────────────────────────────
// Each comparator takes two fn_work_list items and returns a sort-comparison
// integer. getSortComparator(sortField) maps a sort key to its comparator,
// defaulting to urgency so the existing default behavior is preserved when
// sortField === "urgency" (or any unrecognized value). "Null sorts to end"
// means a missing/unparseable value sinks below every present value, with ties
// kept stable (return 0).

// Urgency — reuse the existing ordering above. Urgency is the default axis.
// Direction is now honored (Item 4): "desc" keeps the most-urgent-first default,
// "asc" reverses it so the least-urgent items surface first.
export const sortByUrgency = (a, b, direction = "desc") => sortItemsByUrgency(a, b, direction);

// Due date — ascending (soonest first) by default. Null/missing `due` sinks to
// end regardless of direction. Pass direction="desc" to flip to latest first.
export const sortByDueDate = (a, b, direction = "asc") => {
  const aDue = a?.due ?? null;
  const bDue = b?.due ?? null;
  if (aDue == null && bDue == null) return 0;
  if (aDue == null) return 1;   // a sinks
  if (bDue == null) return -1;  // b sinks
  const aT = new Date(aDue).getTime();
  const bT = new Date(bDue).getTime();
  if (Number.isNaN(aT) && Number.isNaN(bT)) return 0;
  if (Number.isNaN(aT)) return 1;
  if (Number.isNaN(bT)) return -1;
  const base = aT - bT;
  return direction === "desc" ? -1 * base : base;
};

// Priority — high to low by default. ClickUp stores priority as a JSON string
// like {"id":"2","color":"#f8ae00","priority":"high","orderindex":"2"}. Lower
// orderindex = higher priority, so sort ascending by default. Pass
// direction="desc" to flip to low-to-high. Null/unparseable sinks to end.
const parsePriorityOrder = (item) => {
  const raw = item?.priority;
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") {
    const o = Number(raw.orderindex);
    return Number.isFinite(o) ? o : null;
  }
  try {
    const parsed = JSON.parse(raw);
    const o = Number(parsed?.orderindex);
    return Number.isFinite(o) ? o : null;
  } catch {
    return null;
  }
};

export const sortByPriority = (a, b, direction = "desc") => {
  const ao = parsePriorityOrder(a);
  const bo = parsePriorityOrder(b);
  if (ao == null && bo == null) return 0;
  if (ao == null) return 1;   // a sinks
  if (bo == null) return -1;  // b sinks
  const base = ao - bo;       // ascending: lower orderindex first (high priority first)
  return direction === "desc" ? -1 * base : base;
};

// Days in status — descending (longest first) by default. Null sinks to end.
// Pass direction="asc" to flip to shortest first.
export const sortByDaysInStatus = (a, b, direction = "desc") => {
  const aDays = a?.days_in_status ?? null;
  const bDays = b?.days_in_status ?? null;
  if (aDays == null && bDays == null) return 0;
  if (aDays == null) return 1;
  if (bDays == null) return -1;
  const base = bDays - aDays;        // descending by default
  return direction === "desc" ? base : -1 * base;
};

// Owner — ascending A–Z by owner name by default. Null sinks to end.
export const sortByOwner = (a, b, direction = "asc") => {
  const aName = a?.owner ?? null;
  const bName = b?.owner ?? null;
  if (aName == null && bName == null) return 0;
  if (aName == null) return 1;
  if (bName == null) return -1;
  const base = String(aName).localeCompare(String(bName));
  return direction === "desc" ? -1 * base : base;
};

// Title — ascending A–Z by default.
export const sortByTitle = (a, b, direction = "asc") => {
  const aTitle = a?.title ?? "";
  const bTitle = b?.title ?? "";
  const base = String(aTitle).localeCompare(String(bTitle));
  return direction === "desc" ? -1 * base : base;
};

// Map a SORT_OPTIONS key to its comparator. Unknown/missing keys fall back to
// urgency so the default ordering never breaks. The returned comparator is
// pre-bound to `direction` so callers can use it directly with Array.sort.
// All comparators (including urgency) honor `direction`.
export const getSortComparator = (sortField, direction) => {
  let base;
  switch (sortField) {
    case "due_date":        base = sortByDueDate;       break;
    case "priority":        base = sortByPriority;       break;
    case "days_in_status":  base = sortByDaysInStatus;   break;
    case "owner":           base = sortByOwner;         break;
    case "title":           base = sortByTitle;         break;
    case "urgency":
    default:                base = sortByUrgency;        break;
  }
  return (a, b) => base(a, b, direction);
};
